import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowDownAZ, ArrowUpAZ, Filter, Check } from "lucide-react";

export type SortState = { key: string; dir: "asc" | "desc" } | null;

type Props = {
  /** Column key used for sort/filter state. */
  columnKey: string;
  label: string;
  /** All distinct display values available in this column (unfiltered by this column). */
  values: string[];
  /** Currently selected values; empty set means "show all". */
  selected: Set<string>;
  onSelectedChange: (next: Set<string>) => void;
  sort: SortState;
  onSortChange: (next: SortState) => void;
  align?: "left" | "right";
};

/** Excel-style column header: click the funnel to sort and pick values. */
export default function ColumnFilter({
  columnKey,
  label,
  values,
  selected,
  onSelectedChange,
  sort,
  onSortChange,
  align = "left",
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const distinct = useMemo(() => {
    const set = new Set(values.map((v) => (v ?? "").toString().trim() || "(blank)"));
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [values]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? distinct.filter((v) => v.toLowerCase().includes(q)) : distinct;
  }, [distinct, search]);

  const active = selected.size > 0;
  const sorted = sort?.key === columnKey;

  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onSelectedChange(next);
  };

  return (
    <div className={`flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
      <span>{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Filter and sort ${label}`}
            className={`rounded p-0.5 transition-colors ${
              active || sorted ? "text-primary" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {sorted ? (
              sort?.dir === "asc" ? (
                <ArrowUpAZ className="h-3 w-3" />
              ) : (
                <ArrowDownAZ className="h-3 w-3" />
              )
            ) : (
              <Filter className={`h-3 w-3 ${active ? "fill-current" : ""}`} />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-2">
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 flex-1 justify-start px-1 text-[11px]"
              onClick={() => onSortChange({ key: columnKey, dir: "asc" })}
            >
              <ArrowUpAZ className="mr-1 h-3 w-3" /> Sort A→Z
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 flex-1 justify-start px-1 text-[11px]"
              onClick={() => onSortChange({ key: columnKey, dir: "desc" })}
            >
              <ArrowDownAZ className="mr-1 h-3 w-3" /> Sort Z→A
            </Button>
          </div>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search values…"
            className="mt-2 h-7 text-xs"
          />
          <div className="mt-2 max-h-52 space-y-0.5 overflow-auto">
            {shown.length === 0 && (
              <p className="py-2 text-center text-[11px] text-muted-foreground">No values</p>
            )}
            {shown.map((v) => {
              const checked = selected.size === 0 || selected.has(v);
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => toggle(v)}
                  className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left text-[11px] hover:bg-accent"
                >
                  <span
                    className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border ${
                      selected.has(v) ? "border-primary bg-primary text-primary-foreground" : "border-border"
                    }`}
                  >
                    {selected.has(v) && <Check className="h-2.5 w-2.5" />}
                  </span>
                  <span className={`truncate ${checked ? "" : "text-muted-foreground"}`}>{v}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex gap-1 border-t pt-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 flex-1 text-[11px]"
              onClick={() => onSelectedChange(new Set(shown))}
            >
              Select all
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 flex-1 text-[11px]"
              onClick={() => {
                onSelectedChange(new Set());
                if (sorted) onSortChange(null);
              }}
            >
              Clear
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
